import React, { useCallback, useState } from 'react';

import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { CommerceEmbed } from './CommerceEmbed';
import { PollingForUpgrade } from './PollingForUpgrade';

export type Edition = 'free' | 'standard' | 'premium';

export type Product = 'jira' | 'jira-service-management' | 'jira-product-discovery' | 'confluence';

export function PlaceOrderLite({
	href,
	product,
	edition,
	onComplete,
	onOrderPlaced,
	onError,
	onMessage,
	onSecondaryAction,
	secondaryAction,
	primaryAction,
	actionsAlignment,
	trialTrigger,
	trialType,
}: {
	actionsAlignment?: 'vertical' | 'horizontal';
	edition: Edition;
	/** This is the iframe that will be loaded, usually requested through the entitlement experience capability `placeOrderLite` */
	href: string;
	/** Called when the upgrade has completed. */
	onComplete: () => void;
	onError: (error: Error) => void;
	/** Forwards the parent messages from the iframe */
	onMessage?: (message: Record<string, string>) => void;
	onOrderPlaced?: () => void;
	onSecondaryAction?: () => void;
	/** When passed through, this will override the button text for the primary action */
	primaryAction?: string;
	product: Product;
	/**
	 * When passed through, this will show a secondary action with the text content.
	 * When clicked it will call `onSecondaryAction` if it exists.
	 */
	secondaryAction?: string;
	/** Should be used ff starting a trial that is not counted as triggered by UI */
	trialTrigger?: string;
	/** Should be used ff starting a trial that is not counted as a manual trial */
	trialType?: string;
}): React.JSX.Element {
	const [hasUpgraded, setHasUpgraded] = useState(false);

	const handleOnOrderComplete = useCallback(() => {
		setHasUpgraded(true);
		onOrderPlaced?.();
	}, [onOrderPlaced]);

	if (!hasUpgraded) {
		return (
			<CommerceEmbed
				onError={onError}
				onComplete={handleOnOrderComplete}
				onMessage={onMessage}
				href={href}
				product={product}
				onSecondaryAction={onSecondaryAction}
				secondaryAction={secondaryAction}
				primaryAction={primaryAction}
				actionsAlignment={actionsAlignment}
				trialTrigger={trialTrigger}
				trialType={trialType}
			/>
		);
	}

	return (
		<AtlassianRelayEnvironmentProvider>
			<PollingForUpgrade
				onComplete={onComplete}
				onError={onError}
				product={product}
				edition={edition}
			/>
		</AtlassianRelayEnvironmentProvider>
	);
}
